import * as SyncAnalysisStore from '../../../../redux/middleware/ProtocolConsumptionSyncAnalysisMiddleware';

import { AppState } from '../../../../redux/reducers';
import {
    SyncAnalysis,
    SyncAnalysisFinding,
} from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisStatusEnum } from '../../../../models/Enums/SyncAnalysisStatusEnum';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { PaginationState } from '../../../../models/PaginationState';
import { ErrorType } from '../../../../models/HttpError';
import { EmptyFindingsPaginationState } from '../../../../redux/states/ProtocolConsumptionSyncAnalysis.States';

//#region Props

export interface IProps {
    /** Corrida de sincronización sobre la que se pide el análisis. */
    SyncProcessId: string;
    /**
     * La corrida terminó bien. Es condición para ofrecer el análisis a mano: una
     * corrida a medio hacer no tiene todavía qué analizar, y es recién al terminar
     * cuando pide sola el análisis automático.
     */
    SyncProcessFinished: boolean;
    isOpen: boolean;
    onDismiss: () => void;
}

interface ConnectedProps {
    /** undefined = todavía no se consultó; null = la corrida no fue analizada. */
    analysis: SyncAnalysis | null | undefined;
    isGettingAnalysis: boolean;
    failOnGettingAnalysis: boolean;

    isExecutingAnalysis: boolean;
    failOnExecutingAnalysis: boolean;
    executionError: ErrorType | undefined;

    findings: SyncAnalysisFinding[];
    findingsPagination: PaginationState;
    isGettingFindings: boolean;
    failOnGettingFindings: boolean;

    error: ErrorType | undefined;
}

interface DispatchProps {
    GetProtocolConsumptionSyncAnalysis: typeof SyncAnalysisStore.ActionCreators.GetProtocolConsumptionSyncAnalysis;
    GetProtocolConsumptionSyncAnalysisFindings: typeof SyncAnalysisStore.ActionCreators.GetProtocolConsumptionSyncAnalysisFindings;
    ExecuteProtocolConsumptionSyncAnalysis: typeof SyncAnalysisStore.ActionCreators.ExecuteProtocolConsumptionSyncAnalysis;
    RefreshProtocolConsumptionSyncAnalysis: typeof SyncAnalysisStore.ActionCreators.RefreshProtocolConsumptionSyncAnalysis;
}

export type Props = IProps & ConnectedProps & DispatchProps;

//#endregion Props

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const slice = state.ProtocolConsumptionSyncAnalysis;
    // El análisis se busca por corrida: el slice guarda uno por syncProcessId
    // para que abrir el panel sobre otra corrida no muestre el anterior.
    const analysis = slice?.analysisBySyncProcessId?.[ownProps.SyncProcessId];
    const analysisId = analysis?.id;
    const isThisExecution = slice?.executionSyncProcessId === ownProps.SyncProcessId;

    return {
        analysis,
        isGettingAnalysis: slice?.isGettingAnalysis || false,
        failOnGettingAnalysis: slice?.failOnGettingAnalysis || false,

        // Las banderas de ejecución son del slice entero: sólo valen para el panel
        // de la corrida sobre la que se pidió el análisis.
        isExecutingAnalysis: isThisExecution && (slice?.isExecutingAnalysis || false),
        failOnExecutingAnalysis: isThisExecution && (slice?.failOnExecutingAnalysis || false),
        executionError: isThisExecution ? slice?.executionError : undefined,

        findings: (analysisId && slice?.findingsByAnalysisId?.[analysisId]) || [],
        findingsPagination:
            (analysisId && slice?.findingsPaginationByAnalysisId?.[analysisId]) ||
            EmptyFindingsPaginationState,
        isGettingFindings: slice?.isGettingFindings || false,
        failOnGettingFindings: slice?.failOnGettingFindings || false,

        error: slice?.error,
    } as ConnectedProps;
};

export const mapDispatchToProps = {
    ...SyncAnalysisStore.ActionCreators,
};

//#region Presentación

export type AnalysisStatusVariant = 'pending' | 'running' | 'success' | 'error' | 'skipped';

export const getStatusVariant = (status?: SyncAnalysisStatusEnum): AnalysisStatusVariant => {
    switch (status) {
        case SyncAnalysisStatusEnum.Pending:
            return 'pending';
        case SyncAnalysisStatusEnum.InProgress:
            return 'running';
        case SyncAnalysisStatusEnum.Completed:
            return 'success';
        case SyncAnalysisStatusEnum.Failed:
            return 'error';
        case SyncAnalysisStatusEnum.SkippedNoIndicators:
            return 'skipped';
        default:
            return 'pending';
    }
};

export const STATUS_LABELS: Record<SyncAnalysisStatusEnum, string> = {
    [SyncAnalysisStatusEnum.Pending]: 'Pendiente',
    [SyncAnalysisStatusEnum.InProgress]: 'En curso',
    [SyncAnalysisStatusEnum.Completed]: 'Completado',
    [SyncAnalysisStatusEnum.Failed]: 'Falló',
    [SyncAnalysisStatusEnum.SkippedNoIndicators]: 'Sin indicios',
};

export const getStatusLabel = (status?: SyncAnalysisStatusEnum): string =>
    status !== undefined && STATUS_LABELS[status] !== undefined
        ? STATUS_LABELS[status]
        : 'Estado desconocido';

export interface FindingTypeDescriptor {
    type: SyncAnalysisFindingTypeEnum;
    label: string;
    /** Qué mira este tipo de hallazgo, para quien no conoce el catálogo. */
    description: string;
    /** Dónde ancla el hallazgo: sirve para saber sobre qué se actúa. */
    anchor: string;
}

export const FINDING_TYPES: FindingTypeDescriptor[] = [
    {
        type: SyncAnalysisFindingTypeEnum.VisitWithoutConsumption,
        label: 'Visita sin consumo',
        description: 'Visita evaluada que no generó ningún consumo.',
        anchor: 'Visita del EMR',
    },
    {
        type: SyncAnalysisFindingTypeEnum.UnsettledFormItem,
        label: 'Ítem sin liquidar',
        description: 'Ítem del formulario realizado y facturable que no derivó en consumo.',
        anchor: 'Visita del EMR e ítem',
    },
    {
        type: SyncAnalysisFindingTypeEnum.MisconfiguredConcept,
        label: 'Concepto mal parametrizado',
        description: 'Concepto cuya parametrización le impide liquidar.',
        anchor: 'Concepto de contrato',
    },
    {
        type: SyncAnalysisFindingTypeEnum.ContradictoryConfiguration,
        label: 'Parametrización contradictoria',
        description: 'Combinación de parametrización internamente contradictoria.',
        anchor: 'Concepto de contrato',
    },
];

export const getFindingTypeDescriptor = (
    type?: SyncAnalysisFindingTypeEnum
): FindingTypeDescriptor | undefined => FINDING_TYPES.find(item => item.type === type);

export const getFindingTypeLabel = (type?: SyncAnalysisFindingTypeEnum): string =>
    getFindingTypeDescriptor(type)?.label ?? `Tipo ${type ?? '—'}`;

/**
 * Resuelve una clave de `findingCountsByType` al valor del enum. El servidor
 * puede serializarla como número (`"1"`) o como nombre (`"VisitWithoutConsumption"`),
 * así que se contemplan las dos formas antes de descartar la clave.
 */
export const resolveFindingTypeKey = (key: string): SyncAnalysisFindingTypeEnum | undefined => {
    const asNumber = Number(key);
    if (Number.isFinite(asNumber) && getFindingTypeDescriptor(asNumber) !== undefined) {
        return asNumber as SyncAnalysisFindingTypeEnum;
    }

    const byName = (SyncAnalysisFindingTypeEnum as unknown as Record<string, number>)[key];
    return typeof byName === 'number' ? (byName as SyncAnalysisFindingTypeEnum) : undefined;
};

export interface FindingTypeCount {
    descriptor: FindingTypeDescriptor;
    count: number;
}

/**
 * Contadores por tipo, en el orden del catálogo y no en el que llegaron, para
 * que el mismo tipo esté siempre en el mismo lugar entre una corrida y otra.
 */
export const buildFindingTypeCounts = (analysis?: SyncAnalysis | null): FindingTypeCount[] => {
    const counts = analysis?.findingCountsByType;
    if (!counts) return [];

    const byType = new Map<SyncAnalysisFindingTypeEnum, number>();
    Object.keys(counts).forEach(key => {
        const type = resolveFindingTypeKey(key);
        if (type === undefined) return;
        byType.set(type, (byType.get(type) ?? 0) + (counts[key] ?? 0));
    });

    return FINDING_TYPES.filter(descriptor => byType.has(descriptor.type)).map(descriptor => ({
        descriptor,
        count: byType.get(descriptor.type) ?? 0,
    }));
};

/** Total de hallazgos según los contadores por tipo del resumen. */
export const getTotalFindingCount = (analysis?: SyncAnalysis | null): number =>
    buildFindingTypeCounts(analysis).reduce((total, item) => total + item.count, 0);

/**
 * El análisis todavía puede cambiar solo: el servidor lo persiste en curso apenas
 * arranca y lo reescribe al terminar.
 */
export const isAnalysisRunning = (analysis?: SyncAnalysis | null): boolean =>
    analysis?.status === SyncAnalysisStatusEnum.Pending ||
    analysis?.status === SyncAnalysisStatusEnum.InProgress;

/**
 * Cuánto se espera al análisis automático antes de ofrecer pedirlo a mano.
 *
 * Al terminar bien, la corrida publica el pedido del análisis al bus, así que la
 * fila tarda unos segundos en aparecer. Ofrecer el disparo manual antes de eso
 * llevaría al usuario a pedir algo que ya venía en camino, y el servidor se lo
 * rechazaría por duplicado.
 */
export const SYNC_ANALYSIS_AUTOMATIC_GRACE_MS = 60000;

/**
 * Cada cuánto se relee un análisis en curso. Corto para que el panel acompañe la
 * corrida, pero no tanto como para castigar a la API mientras el análisis trabaja.
 */
export const SYNC_ANALYSIS_POLL_INTERVAL_MS = 5000;

/**
 * Tope de relecturas seguidas: unos diez minutos. Un análisis que quedó marcado en
 * curso porque el proceso murió no se despierta nunca, y sin este tope el panel
 * abierto sondearía para siempre.
 */
export const SYNC_ANALYSIS_MAX_POLLS = 120;

/** Un análisis sin terminar no tiene hallazgos que listar. */
export const hasFindingsToShow = (analysis?: SyncAnalysis | null): boolean =>
    !!analysis && analysis.status === SyncAnalysisStatusEnum.Completed;

//#endregion Presentación
